import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { usePDF } from '@react-pdf/renderer'
import { Alert, AlertTitle } from '@material-ui/lab'
import { useTranslation } from 'react-i18next'

import MultiAlarmPreview from 'src/parser/entities/Alarm/Preview/MultiAlarmPreview'
import AlarmReportCollector from 'src/parser/entities/Alarm/List/AlarmReportCollector'
import useAlarmReportProps, {
  MAX_CONSOLIDATED_ALARMS,
} from 'src/parser/entities/Alarm/List/useAlarmReportProps'
import useBulkDownloadZip from 'src/parser/entities/Alarm/List/useBulkDownloadZip'
import Notification from 'src/adminPanel/layout/Notification'
import type { TAlarmReportProps } from 'src/parser/shared/PDF/Provider'
import {
  dominantFailureCode,
  exportErrorMessageKey,
} from 'src/parser/entities/Alarm/Export/exportErrors'
import PDFLoading from './PDFLoading'
import { PDFViewer } from './PDFViewer/PDFViewer'
import { useRoundButtonStyles } from './PDFViewer/useRoundButtonStyles'
import { resolveReportAuthorization } from './reportAuthorization'
import { languageFromParam, toI18nLanguage } from './pdfLanguage'

const parseIds = (ids: string | null): string[] =>
  (ids ?? '')
    .split(',')
    .map(id => id.trim())
    .filter(id => id.length > 0)

const viewerFrame: React.CSSProperties = { height: 'auto', border: 'none' }

const ConsolidatedDocument = ({
  reports,
}: {
  reports: TAlarmReportProps[]
}): JSX.Element => {
  const { t } = useTranslation()
  const [pdf] = usePDF({ document: <MultiAlarmPreview reports={reports} /> })
  const classes = useRoundButtonStyles()

  const [downloadZip, exportState] = useBulkDownloadZip(reports, pdf.blob)

  if (pdf.error !== null)
    return (
      <Alert severity='error'>
        <AlertTitle>{t('error.pdfCreationFailed')}</AlertTitle>
        {String(pdf.error)}
      </Alert>
    )

  if (pdf.url === null) return <PDFLoading />

  return (
    <>
      <PDFViewer
        src={`${pdf.url}#toolbar=1`}
        pdf={pdf}
        title={t('alarm_report.viewer.title', { count: reports.length }) ?? ''}
        iframeStyle={viewerFrame}
        showDownloadButton
        loading={exportState.status === 'running'}
        buttonClasses={classes}
        onDownload={downloadZip}
      />
      <Notification />
    </>
  )
}

const fill: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  height: '100vh',
}

export const ConsolidatedAlarmPDF = (): JSX.Element => {
  const { t, i18n } = useTranslation()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const ids = parseIds(params.get('ids'))
  const tooMany = ids.length > MAX_CONSOLIDATED_ALARMS
  const lang = params.get('lang')
  const access = resolveReportAuthorization(
    params.get('authorization'),
    localStorage.getItem('token'),
  )
  const authorization =
    access.status === 'ok' ? access.authorization : undefined

  const { status, entities, reports, failures, onReady } = useAlarmReportProps(
    access.status === 'missing' || tooMany ? [] : ids,
    authorization,
  )

  useEffect(() => {
    const language = languageFromParam(lang)
    if (language !== undefined)
      void i18n.changeLanguage(toI18nLanguage(language))
  }, [lang, i18n])

  if (ids.length === 0)
    return (
      <Alert severity='info'>{t('alarm_report.viewer.no_selection')}</Alert>
    )

  if (access.status === 'missing')
    return (
      <Alert severity='error'>
        <AlertTitle>{t('alarm_report.viewer.fetch_failed')}</AlertTitle>
        {t('alarm_report.viewer.invalid_authorization')}
      </Alert>
    )

  if (tooMany)
    return (
      <Alert severity='warning'>
        {t('alarm_report.viewer.too_many', {
          count: ids.length,
          max: MAX_CONSOLIDATED_ALARMS,
        })}
      </Alert>
    )

  const settled = status === 'ready'

  return (
    <div style={fill}>
      {entities.map(entity => (
        <AlarmReportCollector
          key={entity.id}
          entity={entity}
          authorization={authorization}
          onReady={onReady}
        />
      ))}

      {!settled && <PDFLoading />}

      {settled && reports.length === 0 && (
        <Alert severity='error'>
          <AlertTitle>{t('alarm_report.viewer.all_failed')}</AlertTitle>
          {t(exportErrorMessageKey(dominantFailureCode(failures) ?? 'UNKNOWN'))}
        </Alert>
      )}

      {settled && reports.length > 0 && (
        <>
          {failures.length > 0 && (
            <Alert severity='warning'>
              {t('alarm_report.viewer.partial', { count: failures.length })}
            </Alert>
          )}

          <ConsolidatedDocument
            key={reports.map(report => report.entity.id).join(',')}
            reports={reports}
          />
        </>
      )}
    </div>
  )
}

export default ConsolidatedAlarmPDF
